/*
  What a to-do actually is, in words a person can read.

  Written 5 September 2026 for Group C's Tasks screen. Everything here exists
  because the app was showing a person the inside of its own database:

  - **A title with the location glued to the front of it.** "Location picked up:
    SAT-ASPEN GROVE REGIONAL CLINIC SOUTH" is a sentence and an address welded
    together, and it printed the territory code twice once the card had a line
    for it. Every open to-do carries a `warehouse_id`, so the location can be
    looked up properly and the kind of thing it is can come from `todo_type`.

  - **A note that says your own name back to you.** "Michelle Chelette has
    started covering SAT-ASPEN GROVE REGIONAL CLINIC SOUTH and it was not
    assigned to anybody", read by Michelle Chelette on her own Tasks screen.
    Three of the four kinds were written that way, for an administrator's eye,
    and then shown to the person who raised them.

  - **A raw database value.** "Reason: moved_locations", on screen, to a person.
    Three codes, and none of them had ever been turned into English anywhere,
    including on the administrator's own Review Hub.

  The person's OWN words are kept wherever they typed any: an edit request's
  reason and a closure's notes are theirs and are shown as they wrote them.
*/

// `metadata` is a jsonb column that every writer fills with `JSON.stringify`,
// so what comes back is a JSON string inside a JSON value rather than an
// object. Parsed in one place, and never allowed to throw: a to-do with
// unreadable metadata still has a kind, a location and a date.
export function metaOf(todo) {
  try { return JSON.parse(todo?.metadata || '{}') || {}; } catch { return {}; }
}

// **A picked-up location has no type of its own.** It is written as `general`,
// which is also the column's default, so an untyped row and a picked-up one are
// the same thing to this app. The Review Hub has read it that way since it was
// built; this agrees with it rather than inventing a second rule.
export const PICKED_UP = 'picked_up';

export function kindOf(todo) {
  const t = todo?.todo_type;
  if (!t || t === 'general') return PICKED_UP;
  return t;
}

// The chip on the card. Sentence case, like every other label in OnHand.
export const KIND_LABEL = {
  [PICKED_UP]:          'Picked up',
  edit_request:         'Edit request',
  ownership_transfer:   'Transfer request',
  location_closure:     'Closure flagged',
  count_approval:       'Count to approve',
};

// Its colour, from the same four-plus-two palette the rest of the app uses.
export const KIND_TONE = {
  [PICKED_UP]:          { fg: 'var(--info-ink)', bg: 'var(--info-bg)' },
  edit_request:         { fg: 'var(--accent-ink)',   bg: 'var(--accent-bg)' },
  ownership_transfer:   { fg: 'var(--info-ink)',   bg: 'var(--info-bg)' },
  location_closure:     { fg: 'var(--critical-ink)',         bg: 'var(--critical-bg)' },
  count_approval:       { fg: 'var(--success-ink)',    bg: 'var(--success-bg)' },
};

/*
  Why a location was flagged as closing, in words.

  The three codes are what the closure form writes. They were shown raw on the
  rep's Tasks screen and on the administrator's Review Hub, so "moved_locations"
  was a thing a person read on both.
*/
export const CLOSURE_REASON = {
  practice_closed:  'The practice has closed',
  moved_locations:  'They have moved',
  no_longer_stocks: 'They no longer stock our items',
};

export function closureReason(code) {
  if (!code) return '';
  return CLOSURE_REASON[code] || code.replace(/_/g, ' ');
}

/*
  What one to-do says on the person's own Tasks screen, in the second person.

  **The note is theirs where they typed one and ours where they did not.** An
  edit request and a closure both ask for a reason in the person's own words and
  those are shown exactly as written. A picked-up location and a transfer request
  ask for nothing, so the sentence is written here rather than left as the
  machine text an administrator was meant to read.
*/
export function noteFor(todo) {
  const meta = metaOf(todo);
  switch (kindOf(todo)) {
    case 'edit_request':
      return meta.reason || todo.description || 'You asked for this sheet to be reopened.';
    case 'location_closure':
      return metaOf(todo).notes || 'You flagged this location as closing.';
    case 'ownership_transfer':
      return meta.current_owner_name
        ? 'You asked to take this on from ' + meta.current_owner_name + '.'
        : 'You asked to take this location on.';
    case PICKED_UP:
    default:
      return 'You started covering this and it was not assigned to anybody.';
  }
}

/*
  The one extra line a card carries under the note, where the kind of thing has
  one. Today that is a closure and the reason the person picked off the form.

  **It is its own line rather than tacked onto the note in brackets**, because
  the note is the person's own sentence and often already says it: "They moved
  two miles up the road. New address is on the door. (they have moved)" is the
  app talking over somebody.
*/
export function asideFor(todo) {
  if (kindOf(todo) !== 'location_closure') return '';
  return closureReason(metaOf(todo).reason);
}
